/**
 * Shared layout for audience-targeted landing pages
 * (/family-offices and /for-funds). Audience-specific copy comes from
 * `audienceLandingConfig`. Apple-style: alternating bands, generous
 * whitespace, framer-motion fade-ups.
 *
 * AudienceIntakeChat is embedded directly in the hero (Claude-driven
 * conversational intake with server-side validation + deterministic
 * tier scoring). UTM params captured on mount are forwarded into the
 * chat session so attribution flows through to the lead row written at
 * conversation close.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AudienceIntakeChat from '../components/AudienceIntakeChat';
import type { AudienceLandingConfig } from '../data/audienceLandingConfig';

/** UTM params captured from the URL on landing-page mount. Persisted to
 *  sessionStorage so internal navigation (e.g., opening the intake
 *  modal in a new viewport) doesn't lose attribution. Forwarded to
 *  /leads/submit on intake submission so the lead row records who
 *  sent the traffic. */
export type UtmParams = Partial<Record<
  'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content' | 'utm_term',
  string
>>;

const UTM_KEYS: Array<keyof UtmParams> = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
];
const UTM_STORAGE_KEY = 'utm_params';

function readUtmFromUrl(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  const out: UtmParams = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
}

function readUtmFromStorage(): UtmParams {
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.9, ease: 'easeOut' as const } },
};

export const TopBar: React.FC = () => (
  <header className="fixed inset-x-0 top-0 z-40 bg-apple-bg/80 backdrop-blur supports-[backdrop-filter]:bg-apple-bg/70">
    <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-6">
      <Link to="/" className="text-sm font-medium text-apple-ink">
        VentureOS
      </Link>
      <Link
        to="/signin"
        className="text-sm text-apple-subtext transition-colors hover:text-apple-ink"
      >
        Sign In
      </Link>
    </div>
  </header>
);

export const Hero: React.FC<{
  config: AudienceLandingConfig;
  utm: UtmParams;
}> = ({ config, utm }) => (
  // The chat is the "host" of the landing experience -- it's embedded
  // directly into the hero rather than hidden behind a CTA so the
  // visitor's first action is a conversation, not a click. The id lets
  // in-page CTAs (e.g. the investor "chat now" card) scroll back here.
  <section id="hero-chat" className="relative min-h-screen bg-apple-bg px-6 pt-24 pb-16">
    <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
      <motion.div initial="hidden" animate="show" variants={fadeIn} className="text-center lg:text-left">
        <motion.p
          variants={fadeUp}
          className="text-sm font-medium uppercase tracking-widest text-apple-subtext"
        >
          {config.heroEyebrow}
        </motion.p>
        <motion.h1
          variants={fadeUp}
          transition={{ delay: 0.1 }}
          className="mt-6 text-5xl font-semibold tracking-tight text-apple-ink sm:text-display-2 md:text-display-1"
        >
          {config.heroHeadline}
        </motion.h1>
        <motion.p
          variants={fadeUp}
          transition={{ delay: 0.2 }}
          className="mx-auto mt-6 max-w-2xl text-lg text-apple-subtext sm:text-subhead lg:mx-0"
        >
          {config.heroSub}
        </motion.p>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="show"
        variants={fadeUp}
        transition={{ delay: 0.2 }}
        className="lg:pl-6"
      >
        <AudienceIntakeChat
          open
          mode="inline"
          audience={config.slug}
          audienceLabel={config.audienceLabel}
          utm={utm}
        />
      </motion.div>
    </div>
  </section>
);

export const ProofRow: React.FC<{ config: AudienceLandingConfig }> = ({ config }) => (
  <section className="bg-apple-bg px-6 py-section">
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      className="mx-auto grid max-w-5xl gap-10 text-center sm:grid-cols-3"
    >
      {config.proofs.map((p, i) => (
        <motion.div key={i} variants={fadeUp} transition={{ delay: i * 0.1 }}>
          <p className="text-5xl font-semibold tracking-tight text-apple-ink sm:text-display-2">
            {p.metric}
          </p>
          <p className="mt-3 text-sm text-apple-subtext sm:text-base">{p.label}</p>
        </motion.div>
      ))}
    </motion.div>
  </section>
);

export const FeatureBand: React.FC<{
  feature: AudienceLandingConfig['features'][number];
  dark?: boolean;
}> = ({ feature, dark }) => (
  <section
    className={`flex min-h-[80vh] items-center px-6 py-section ${
      dark ? 'bg-apple-black text-white' : 'bg-apple-bg text-apple-ink'
    }`}
  >
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      className="mx-auto max-w-4xl text-center"
    >
      <p
        className={`text-sm font-medium uppercase tracking-widest ${
          dark ? 'text-white/60' : 'text-apple-subtext'
        }`}
      >
        {feature.eyebrow}
      </p>
      <h2 className="mt-4 text-4xl font-semibold tracking-tight sm:text-headline md:text-display-2">
        {feature.headline}
      </h2>
      <p
        className={`mx-auto mt-6 max-w-2xl text-lg sm:text-subhead ${
          dark ? 'text-white/70' : 'text-apple-subtext'
        }`}
      >
        {feature.body}
      </p>
    </motion.div>
  </section>
);

export const ClosingCTA: React.FC = () => (
  <section className="flex min-h-[40vh] items-center bg-apple-bg px-6 py-section">
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      className="mx-auto max-w-3xl text-center"
    >
      <h2 className="text-4xl font-semibold tracking-tight text-apple-ink sm:text-display-2">
        Ready to see it on your own deals?
      </h2>
      <p className="mx-auto mt-6 max-w-xl text-lg text-apple-subtext sm:text-subhead">
        Scroll back up and chat with us — about a minute of questions,
        and we'll line up a 15-minute discovery call.
      </p>
    </motion.div>
  </section>
);

export const Footer: React.FC = () => (
  <footer className="border-t border-apple-divider bg-apple-bg px-6 py-12 text-center text-sm text-apple-subtext">
    <p>&copy; {new Date().getFullYear()} VentureOS. All rights reserved.</p>
  </footer>
);

/** Resolve UTM params synchronously. URL params take precedence over
 *  sessionStorage (a fresh tagged link should always overwrite a stale
 *  stored set); when the URL has no UTMs, hydrate whatever we stored on
 *  a prior visit in the same session. Done synchronously (not in a
 *  mount effect) so the `utm` object is stable across renders — a
 *  changing object identity used to re-fire the chat's start effect
 *  and double-POST /public/intake/start. */
export function resolveUtm(): UtmParams {
  const fromUrl = readUtmFromUrl();
  if (Object.keys(fromUrl).length > 0) {
    try { sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fromUrl)); } catch { /* ignore */ }
    return fromUrl;
  }
  return readUtmFromStorage();
}

const AudienceLandingPage: React.FC<{ config: AudienceLandingConfig }> = ({ config }) => {
  // Lazy initializer: runs exactly once, identity never changes.
  const [utm] = useState<UtmParams>(resolveUtm);

  return (
    <main className="bg-apple-bg">
      <TopBar />
      <Hero config={config} utm={utm} />
      <ProofRow config={config} />
      {config.features.map((f, i) => (
        <FeatureBand key={i} feature={f} dark={i % 2 === 0} />
      ))}
      <ClosingCTA />
      <Footer />
    </main>
  );
};

export default AudienceLandingPage;
